<template>
  <div class="tags-parameters">
    <h3>{{ $t('parameters.tags.title') || 'Gestion des Tags' }}</h3>
    <p class="section-description">
      {{ $t('parameters.tags.description') || 'Créez et gérez les tags pour organiser vos postes d\'émission.' }}
    </p>

    <!-- Loading state -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
    </div>

    <!-- Tags list -->
    <div v-else class="tags-management">
      <!-- Create new tag button -->
      <div class="create-tag-section">
        <button 
          class="create-tag-btn"
          @click="showCreateModal = true"
        >
          <font-awesome-icon :icon="['fas', 'plus']" style="color: white;" />
          {{ $t('parameters.tags.createNew') || 'Créer un nouveau tag' }}
        </button>
      </div>

      <!-- Tags grid -->
      <div v-if="tags.length === 0" class="no-tags">
        <div class="empty-state">
          <font-awesome-icon :icon="['fas', 'tags']" class="empty-icon" />
          <h4>{{ $t('parameters.tags.noTags') || 'Aucun tag créé' }}</h4>
          <p>{{ $t('parameters.tags.noTagsDesc') || 'Créez votre premier tag pour commencer à organiser vos postes d\'émission.' }}</p>
        </div>
      </div>

      <div v-else class="tags-grid">
        <div 
          v-for="tag in tags" 
          :key="tag.id"
          class="tag-item"
          :style="{ borderColor: tag.color }"
        >
          <div class="tag-preview" :style="{ backgroundColor: tag.color }">
            {{ tag.name }}
          </div>
          
          <div class="tag-info">
            <h4 class="tag-name">{{ tag.name }}</h4>
            <p class="tag-meta">
              {{ $t('parameters.tags.createdAt') || 'Créé le' }} 
              {{ formatDate(tag.created_at) }}
            </p>
          </div>

          <div class="tag-actions">
            <button 
              class="edit-btn"
              @click="editTag(tag)"
              :title="$t('general.edit') || 'Modifier'"
            >
              <font-awesome-icon :icon="['fas', 'edit']" />
            </button>
            <button 
              class="delete-btn"
              @click="confirmDelete(tag)"
              :title="$t('general.delete') || 'Supprimer'"
            >
              <font-awesome-icon :icon="['fas', 'trash']" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || showEditModal" class="modal-overlay">
      <div class="modal">
        <div class="modal-header">
          <h3>
            {{ showCreateModal ? 
               ($t('parameters.tags.createNew') || 'Créer un nouveau tag') : 
               ($t('parameters.tags.editTag') || 'Modifier le tag') 
            }}
          </h3>
          <button 
            class="close-btn" 
            @click="closeModal"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label>{{ $t('parameters.tags.tagName') || 'Nom du tag' }}</label>
            <input 
              v-model="formData.name" 
              type="text" 
              class="form-input"
              maxlength="100"
              :placeholder="$t('parameters.tags.tagNamePlaceholder') || 'Entrez le nom du tag'"
            />
            <div v-if="formErrors.name" class="error-message">
              {{ formErrors.name }}
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('parameters.tags.tagColor') || 'Couleur du tag' }}</label>
            
            <!-- Couleurs prédéfinies -->
            <div class="color-options">
              <div 
                v-for="color in colorOptions" 
                :key="color"
                class="color-option"
                :class="{ 'selected': formData.color === color }"
                :style="{ backgroundColor: color }"
                @click="formData.color = color"
              ></div>
              
              <!-- Couleur personnalisée -->
              <div class="color-option custom-color-picker" @click="$refs.colorInput.click()">
                <div class="custom-color-gradient"></div>
                <input 
                  ref="colorInput"
                  v-model="formData.color" 
                  type="color" 
                  class="hidden-color-input"
                />
              </div>
            </div>
            
            <!-- Aperçu -->
            <div class="color-preview-container">
              <div 
                class="color-preview" 
                :style="{ backgroundColor: formData.color }"
              >
                {{ formData.name || ($t('parameters.tags.preview') || 'Aperçu') }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button 
            class="cancel-btn" 
            @click="closeModal"
          >
            {{ $t('general.cancel') || 'Annuler' }}
          </button>
          <button 
            class="save-btn" 
            @click="saveTag"
            :disabled="!formData.name.trim() || isSubmitting"
          >
            {{ isSubmitting ? 
               ($t('general.saving') || 'Sauvegarde...') : 
               ($t('general.save') || 'Sauvegarder') 
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="modal">
        <div class="modal-header">
          <h3>{{ $t('parameters.tags.confirmDelete') || 'Confirmer la suppression' }}</h3>
          <button class="close-btn" @click="showDeleteModal = false">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <div class="modal-body">
          <p>
            {{ $t('parameters.tags.deleteWarning') || 'Êtes-vous sûr de vouloir supprimer le tag' }} 
            <strong>"{{ tagToDelete?.name }}"</strong> ?
          </p>
          <p class="warning-text">
            {{ $t('parameters.tags.deleteConsequence') || 'Ce tag sera retiré de tous les postes d\'émission associés.' }}
          </p>
        </div>

        <div class="modal-footer">
          <button class="cancel-btn" @click="showDeleteModal = false">
            {{ $t('general.cancel') || 'Annuler' }}
          </button>
          <button 
            class="delete-confirm-btn" 
            @click="deleteTag"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? 
               ($t('general.deleting') || 'Suppression...') : 
               ($t('general.delete') || 'Supprimer') 
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import instance from '../../main';
import eventBus from '../../eventBus';
import { useCarbonTrajectoryStore } from '../../stores/carbonTrajectory';

export default {
  name: 'tagsParameters',
  data() {
    return {
      isLoading: true,
      tags: [],
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      isSubmitting: false,
      editingTag: null,
      tagToDelete: null,
      formData: {
        name: '',
        color: '#6366f1'
      },
      formErrors: {},
      colorOptions: [
        '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', 
        '#1abc9c', '#e67e22', '#34495e', '#f1c40f', '#e91e63'
      ]
    };
  },
  async mounted() {
    await this.loadTags();
    this.isLoading = false;
  },
  methods: {
    async loadTags() {
      try {
        const response = await instance.get(`/companies/${localStorage.getItem('companyId')}/tags`, {
          headers: {
            "token": localStorage.getItem('token'),
            "company": localStorage.getItem('companyId')
          }
        });
        this.tags = response.data.tags || [];
      } catch (error) {
        console.error("Error loading tags:", error);
        eventBus.emit('show-toast', {
          message: this.$t('toast.loadTagsError') || 'Erreur lors du chargement des tags',
          type: 'error'
        });
      }
    },

    editTag(tag) {
      this.editingTag = tag;
      this.formData.name = tag.name;
      this.formData.color = tag.color || '#6366f1';
      this.showEditModal = true;
      this.formErrors = {};
    },

    confirmDelete(tag) {
      this.tagToDelete = tag;
      this.showDeleteModal = true;
    },

    closeModal() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.editingTag = null;
      this.formData.name = '';
      this.formData.color = '#6366f1';
      this.formErrors = {};
    },

    validateForm() {
      this.formErrors = {};
      
      if (!this.formData.name.trim()) {
        this.formErrors.name = this.$t('parameters.tags.nameRequired') || 'Le nom est requis';
        return false;
      }

      if (this.formData.name.trim().length > 100) {
        this.formErrors.name = this.$t('parameters.tags.nameTooLong') || 'Le nom est trop long (max 100 caractères)';
        return false;
      }

      // Check for duplicate names (except when editing the same tag)
      const duplicateTag = this.tags.find(tag => 
        tag.name.toLowerCase() === this.formData.name.trim().toLowerCase() &&
        tag.id !== this.editingTag?.id
      );
      
      if (duplicateTag) {
        this.formErrors.name = this.$t('parameters.tags.nameExists') || 'Ce nom de tag existe déjà';
        return false;
      }

      return true;
    },

    async saveTag() {
      if (!this.validateForm()) return;
      
      this.isSubmitting = true;
      
      try {
        if (this.showCreateModal) {
          await this.createTag();
        } else {
          await this.updateTag();
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    async createTag() {
      try {
        const response = await instance.post(`/companies/${localStorage.getItem('companyId')}/tags`, {
          name: this.formData.name.trim(),
          color: this.formData.color
        }, {
          headers: {
            "token": localStorage.getItem('token'),
            "company": localStorage.getItem('companyId')
          }
        });
        
        this.tags.push(response.data.tag);
        this.closeModal();
        useCarbonTrajectoryStore().invalidateTagsCache(); // TRAJ-8.27: filtre par tags de /projected
        
        eventBus.emit('show-toast', {
          message: this.$t('toast.tagCreated') || 'Tag créé avec succès',
          type: 'success'
        });
        
      } catch (error) {
        console.error("Error creating tag:", error);
        eventBus.emit('show-toast', {
          message: error.response?.data?.error || this.$t('toast.tagCreateError') || 'Erreur lors de la création du tag',
          type: 'error'
        });
      }
    },

    async updateTag() {
      try {
        const response = await instance.put(`/companies/${localStorage.getItem('companyId')}/tags/${this.editingTag.id}`, {
          name: this.formData.name.trim(),
          color: this.formData.color
        }, {
          headers: {
            "token": localStorage.getItem('token'),
            "company": localStorage.getItem('companyId')
          }
        });
        
        // Update the tag in the list
        const index = this.tags.findIndex(tag => tag.id === this.editingTag.id);
        if (index !== -1) {
          this.tags[index] = response.data.tag;
        }
        
        this.closeModal();
        useCarbonTrajectoryStore().invalidateTagsCache(); // TRAJ-8.27: filtre par tags de /projected
        
        eventBus.emit('show-toast', {
          message: this.$t('toast.tagUpdated') || 'Tag modifié avec succès',
          type: 'success'
        });
        
      } catch (error) {
        console.error("Error updating tag:", error);
        eventBus.emit('show-toast', {
          message: error.response?.data?.error || this.$t('toast.tagUpdateError') || 'Erreur lors de la modification du tag',
          type: 'error'
        });
      }
    },

    async deleteTag() {
      this.isSubmitting = true;
      
      try {
        await instance.delete(`/companies/${localStorage.getItem('companyId')}/tags/${this.tagToDelete.id}`, {
          headers: {
            "token": localStorage.getItem('token'),
            "company": localStorage.getItem('companyId')
          }
        });
        
        // Remove the tag from the list
        this.tags = this.tags.filter(tag => tag.id !== this.tagToDelete.id);
        this.showDeleteModal = false;
        this.tagToDelete = null;
        useCarbonTrajectoryStore().invalidateTagsCache(); // TRAJ-8.27: filtre par tags de /projected
        
        eventBus.emit('show-toast', {
          message: this.$t('toast.tagDeleted') || 'Tag supprimé avec succès',
          type: 'success'
        });
        
      } catch (error) {
        console.error("Error deleting tag:", error);
        eventBus.emit('show-toast', {
          message: error.response?.data?.error || this.$t('toast.tagDeleteError') || 'Erreur lors de la suppression du tag',
          type: 'error'
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    }
  }
};
</script>

<style scoped>
.tags-parameters {
  margin-bottom: 2rem;
}

.section-description {
  color: #6b7280;
  margin-bottom: 1.5rem;
  font-size: 14px;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 2rem;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.create-tag-section {
  margin-bottom: 1.5rem;
}

.create-tag-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.create-tag-btn:hover {
  background: #5856eb;
}

.no-tags {
  text-align: center;
  padding: 3rem 1rem;
}

.empty-state {
  max-width: 300px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 48px;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h4 {
  color: #4b5563;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #6b7280;
  font-size: 14px;
}

.tags-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.tag-item {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.2s ease;
}

.tag-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.tag-preview {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.tag-info {
  margin-bottom: 0.75rem;
}

.tag-name {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
}

.tag-meta {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}

.tag-actions {
  display: flex;
  gap: 8px;
}

.edit-btn, .delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.edit-btn {
  background: #f3f4f6;
  color: #6b7280;
}

.edit-btn:hover {
  background: #e5e7eb;
  color: #4b5563;
}

.delete-btn {
  background: #fef2f2;
  color: #dc2626;
}

.delete-btn:hover {
  background: #fee2e2;
  color: #b91c1c;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #374151;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.error-message {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

.color-options {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.color-option {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.color-option:hover,
.color-option.selected {
  border-color: #333;
  transform: scale(1.1);
}

.custom-color-picker {
  position: relative;
  overflow: hidden;
}

.custom-color-gradient {
  width: 100%;
  height: 100%;
  background: conic-gradient(
    from 0deg,
    #ff0000 0deg,
    #ff8000 45deg,
    #ffff00 90deg,
    #80ff00 135deg,
    #00ff00 180deg,
    #00ff80 225deg,
    #00ffff 270deg,
    #0080ff 315deg,
    #0000ff 360deg
  );
  border-radius: 50%;
}

.hidden-color-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.color-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.color-input {
  width: 40px;
  height: 30px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.custom-color-label {
  font-size: 12px;
  color: #6b7280;
}

.color-preview-container {
  flex: 1;
}

.color-preview {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 20px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  min-width: 100px;
  justify-content: center;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.cancel-btn {
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #f9fafb;
}

.save-btn {
  padding: 10px 20px;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.save-btn:hover:not(:disabled) {
  background: #5856eb;
}

.save-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.delete-confirm-btn {
  padding: 10px 20px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.delete-confirm-btn:hover:not(:disabled) {
  background: #b91c1c;
}

.delete-confirm-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.warning-text {
  color: #f59e0b;
  font-size: 14px;
  background: #fffbeb;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 4px solid #f59e0b;
}
</style>